<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Ticket Notifications");

const rules = ref([]);
const loading = ref(false);
const saving = ref(false);
const deletingId = ref(null);
const notice = ref("");
const error = ref("");
const form = ref({
  match_type: "domain",
  pattern: "",
});

const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const sortedRules = computed(() =>
  [...rules.value].sort((a, b) => {
    const typeCompare = (a?.match_type || "").localeCompare(b?.match_type || "");
    if (typeCompare !== 0) {
      return typeCompare;
    }
    return (a?.pattern || "").localeCompare(b?.pattern || "");
  }),
);

const normalizePattern = () => {
  let value = String(form.value.pattern || "").trim().toLowerCase();
  if (form.value.match_type === "domain") {
    value = value.replace(/^@+/, "").replace(/^\.+|\.+$/g, "");
  }
  return value;
};

const resetMessages = () => {
  notice.value = "";
  error.value = "";
};

const loadRules = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get("/api/super/ticket-notification-exclusions");
    rules.value = Array.isArray(response?.data) ? response.data : [];
  } catch {
    error.value = "Unable to load notification rules.";
  } finally {
    loading.value = false;
  }
};

const addRule = async () => {
  resetMessages();
  const pattern = normalizePattern();

  if (!pattern) {
    error.value = "Pattern is required.";
    return;
  }
  if (form.value.match_type === "domain" && !domainRegex.test(pattern)) {
    error.value = "Enter a valid domain like example.com.";
    return;
  }
  if (form.value.match_type === "contains" && pattern.length < 3) {
    error.value = "Substring must be at least 3 characters.";
    return;
  }

  saving.value = true;
  try {
    const response = await apiClient.post("/api/super/ticket-notification-exclusions", {
      match_type: form.value.match_type,
      pattern,
    });

    if (!response?.data) {
      return;
    }

    form.value.pattern = "";
    notice.value = "Rule saved.";
    await loadRules();
  } catch {
    error.value = "Unable to save rule.";
  } finally {
    saving.value = false;
  }
};

const removeRule = async (rule) => {
  if (!rule?.id) {
    return;
  }

  resetMessages();
  deletingId.value = rule.id;
  try {
    await apiClient.delete(`/api/super/ticket-notification-exclusions/${rule.id}`);
    rules.value = rules.value.filter((entry) => entry.id !== rule.id);
    notice.value = "Rule removed.";
  } catch {
    error.value = "Unable to remove rule.";
  } finally {
    deletingId.value = null;
  }
};

onMounted(loadRules);
</script>

<template>
  <div class="space-y-6">
    <div class="space-y-2">
      <p class="text-xs uppercase tracking-[0.2em] text-slate-400">Super User</p>
      <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">Ticket Notifications</h1>
      <p class="text-sm text-slate-500 dark:text-slate-300">
        Exclude inbound senders from ticket-channel announcements.
      </p>
    </div>

    <div class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Add Exclusion Rule</h2>
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-300">
        Domain rules match sender domain and subdomains. Substring rules match any part of sender details.
      </p>

      <div class="mt-4 grid gap-3 md:grid-cols-[180px_1fr_auto]">
        <select
          v-model="form.match_type"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
        >
          <option value="domain">Domain</option>
          <option value="contains">Substring</option>
        </select>
        <input
          v-model="form.pattern"
          type="text"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-cyan-400 dark:focus:ring-cyan-500/30"
          :placeholder="form.match_type === 'domain' ? 'example.com' : 'no-reply@'"
        />
        <button
          type="button"
          class="btn accent h-10 px-4"
          :disabled="saving"
          @click="addRule"
        >
          {{ saving ? "Saving..." : "Add Rule" }}
        </button>
      </div>

      <p v-if="notice" class="mt-3 text-xs text-emerald-600">{{ notice }}</p>
      <p v-if="error" class="mt-3 text-xs text-rose-600">{{ error }}</p>
    </div>

    <div class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div v-if="loading" class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300">
        Loading rules...
      </div>
      <div v-else-if="!sortedRules.length" class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300">
        No exclusion rules configured.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm text-slate-700 dark:text-slate-200">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-700/40 dark:text-slate-300">
            <tr>
              <th class="px-6 py-3 text-left font-semibold">Type</th>
              <th class="px-6 py-3 text-left font-semibold">Pattern</th>
              <th class="px-6 py-3 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr v-for="rule in sortedRules" :key="rule.id">
              <td class="px-6 py-4">
                <span class="rounded-full border border-slate-200 px-2 py-1 text-xs font-semibold uppercase dark:border-slate-600">
                  {{ rule.match_type }}
                </span>
              </td>
              <td class="px-6 py-4 font-mono text-xs md:text-sm">{{ rule.pattern }}</td>
              <td class="px-6 py-4">
                <button
                  type="button"
                  class="text-sm font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                  :disabled="deletingId === rule.id"
                  @click="removeRule(rule)"
                >
                  {{ deletingId === rule.id ? "Removing..." : "Remove" }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
